/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { lockBodyScroll } from '@/lib/bodyScrollLock';
import { ESCAPE_LEVEL, topOf, useEscapeLayer } from './escapeLayer';

/** An open dialog and the layer it opened on, which Escape uses to pick the one on top too. */
interface OpenDialog {
  el: HTMLElement;
  level: number;
}

/** Open dialogs in the order they opened. Only the top one is live: the app and the rest are inert. */
const openDialogs: OpenDialog[] = [];

/*
 * Inert, not just covered: Tab cannot reach the page or a dialog underneath, and neither can
 * assistive tech. Toasts and tooltips portal beside #root and stay live.
 */
function syncInert() {
  const top = topOf(openDialogs);
  for (const dialog of openDialogs) dialog.el.toggleAttribute('inert', dialog !== top);
  document.getElementById('root')?.toggleAttribute('inert', openDialogs.length > 0);
}

/** An open popup's button (Menu's trigger), which sits beside the popup in the same box. */
const OPEN_POPUP_BUTTON =
  ':scope > [aria-haspopup]:not([aria-haspopup="false"])[aria-expanded="true"]';

/** The button of the popup `el` sits in, if any: an item there goes away with its popup. */
function popupButton(el: Element): HTMLElement | null {
  for (let node = el.parentElement; node; node = node.parentElement) {
    const button = node.querySelector<HTMLElement>(OPEN_POPUP_BUTTON);
    if (button) return button;
  }
  return null;
}

/**
 * Deepest nesting a dialog may ask for. Each level is one step above `--z-overlay` (800), so even
 * the deepest stays far below `--z-overlay-blocking` (850): nothing can cover the blocking
 * disclaimer, and every dialog stays above the header/rail (`--z-header`, `--z-nav`) and the
 * feedback FAB (`--z-fab`). Its Escape level likewise stays under `ESCAPE_LEVEL.blocking`.
 */
const MAX_DEPTH = 9;

function depthLevel(depth: number): number {
  return Math.min(Math.max(Math.round(depth), 0), MAX_DEPTH);
}

/**
 * The scrim's stacking layer, always on the shared `--z-*` scale. A token (`zIndex`, e.g.
 * `var(--z-overlay-blocking)`) wins; otherwise `depth` lifts a dialog opened from another dialog
 * one step per level above `--z-overlay`. With neither, the scrim keeps its stylesheet default
 * (`--z-overlay`), and same-layer dialogs stack in the order they open (portal DOM order).
 */
function modalLayer(zIndex: string | undefined, depth: number): string | undefined {
  if (zIndex) {
    return zIndex;
  }
  const level = depthLevel(depth);
  return level ? `calc(var(--z-overlay) + ${level})` : undefined;
}

/** Backdrop + centered modal shell; locks scroll, closes on Escape/backdrop. */
export function Modal({
  onClose,
  dismissable = true,
  zIndex,
  depth = 0,
  labelledBy,
  children,
}: {
  onClose: () => void;
  dismissable?: boolean;
  /** A z token for a dialog on its own layer, e.g. `var(--z-overlay-blocking)`. */
  zIndex?: string;
  /** How many dialogs this one is opened on top of (0 = opened from the page). */
  depth?: number;
  labelledBy?: string;
  children: ReactNode;
}) {
  // Escape in a confirmation cancels just that, not the dialog it was opened from as well. A
  // token layer counts as above every depth, as `--z-overlay-blocking` is.
  const level = zIndex ? ESCAPE_LEVEL.blocking : ESCAPE_LEVEL.dialog + depthLevel(depth);
  useEscapeLayer(onClose, { level, dismissable });

  useEffect(() => lockBodyScroll(), []);

  // Read while rendering: by the time effects run, a child's autoFocus may have moved focus, and a
  // menu item that opened the dialog has gone with its menu.
  const [returnTo] = useState(() => {
    const opener = document.activeElement as HTMLElement;
    return { opener, button: popupButton(opener) };
  });
  // Where focus sat once the page settled around the opening: a phone drawer that closed as the
  // dialog opened has handed it to its own button, while the opener stays hidden in the drawer.
  const settledRef = useRef<HTMLElement | null>(null);
  // The layer it opened on. No caller moves an open dialog to another layer.
  const [openedOn] = useState(level);
  const scrimRef = useRef<HTMLDivElement>(null);

  // Focus moves in, unless a child took it or a higher layer is up, and goes back on close. The
  // dialog has left the DOM by then, so focus on <body> means it went with it, not that something
  // else took it.
  useEffect(() => {
    const dialog = { el: scrimRef.current as HTMLDivElement, level: openedOn };
    const active = document.activeElement as HTMLElement;
    // A remount (StrictMode) finds focus already inside and keeps the first one.
    if (!dialog.el.contains(active)) settledRef.current = active;
    const settled = settledRef.current;
    openDialogs.push(dialog);
    syncInert();
    if (topOf(openDialogs) === dialog && !dialog.el.contains(active)) {
      dialog.el.focus({ preventScroll: true });
    }
    return () => {
      openDialogs.splice(openDialogs.indexOf(dialog), 1);
      syncInert();
      if (document.activeElement !== document.body) return;
      // While another dialog is up, only somewhere inside it can take focus.
      const top = topOf(openDialogs)?.el;
      for (const back of [returnTo.opener, returnTo.button, settled]) {
        if (!back?.isConnected) continue;
        const target = top && !top.contains(back) ? top : back;
        target.focus({ preventScroll: true });
        // A hidden element turns focus down; the next candidate gets it.
        if (document.activeElement === target) return;
      }
      top?.focus({ preventScroll: true });
    };
  }, [openedOn, returnTo]);

  const layer = modalLayer(zIndex, depth);
  const style = layer ? ({ '--modal-z': layer } as CSSProperties) : undefined;

  // Portal to body so fixed positioning is not clipped by transformed ancestors.
  return createPortal(
    <div
      ref={scrimRef}
      className="scrim"
      style={style}
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelledBy}
      tabIndex={-1}
      onMouseDown={(e) => {
        if (dismissable && e.target === e.currentTarget) onClose();
      }}
    >
      {children}
    </div>,
    document.body,
  );
}
